import { inFormCurrency } from '../../../pricing/catalog-suggestions';
import { panelUnitMoney } from '../../../pricing/panel-format';
import type { PricingSuggestion } from './vetter-prefill';

/**
 * Said once above the tiers when the chosen supplier has answered a price
 * request for this product. Their figures are COST: the sale price stays the
 * admin's to set, and nothing here fills it.
 */
export function VetterQuoteBanner({
	supplierName,
	supplierCurrency,
	offerCurrency,
	convertible,
	note,
	answeredCount,
	pendingCount,
	tierCount,
	onApplyAll,
}: {
	supplierName: string;
	supplierCurrency: string;
	offerCurrency: string;
	/** Whether their figures can be restated in the offer's currency. */
	convertible: boolean;
	note?: string | null;
	answeredCount: number;
	pendingCount: number;
	tierCount: number;
	onApplyAll: () => void;
}) {
	const otherCurrency = supplierCurrency !== offerCurrency;

	return (
		<div className="flex flex-col gap-1.5 rounded-[var(--r-3)] border border-accent-line bg-accent-soft px-2.5 py-1.5 text-xs text-text-1">
			<div className="flex items-center gap-2.5">
				<span>
					<b className="font-medium">From {supplierName}&apos;s price request</b> · their
					cost for {answeredCount} of {tierCount}{' '}
					{tierCount === 1 ? 'quantity' : 'quantities'}. You set the sale price.
					{otherCurrency &&
						(convertible
							? ` They quoted in ${supplierCurrency}; their figures are converted to ${offerCurrency} at today’s rate.`
							: ` They quoted in ${supplierCurrency}, and there is no rate to ${offerCurrency} right now, so their figures can't be applied.`)}
				</span>
				{pendingCount > 0 && convertible && (
					<button
						type="button"
						onClick={onApplyAll}
						className="ml-auto shrink-0 rounded-[var(--r-2)] border border-accent bg-accent px-2 py-0.5 text-2xs text-accent-fg transition-colors hover:bg-accent-hover"
					>
						Use supplier&apos;s costs
					</button>
				)}
			</div>
			{note && (
				<div className="whitespace-pre-line text-text-2">
					<span className="text-text-4">Supplier&apos;s note · </span>
					{note}
				</div>
			)}
		</div>
	);
}

const days = (min?: number | null, max?: number | null) =>
	min == null && max == null ? null : min === max || max == null ? `${min} d` : `${min}–${max} d`;

/**
 * The supplier's answer for one tier, beneath its fields. Before it is used:
 * the figures and a Use button. After: where the cost came from, and a nudge
 * while the sale price is still empty.
 */
export function VetterTierHint({
	sug,
	applied,
	hasSalePrice,
	offerCurrency,
	onUse,
}: {
	sug: PricingSuggestion;
	applied: boolean;
	hasSalePrice: boolean;
	offerCurrency: string;
	onUse: () => void;
}) {
	const supplier = sug.vetterEntity?.name ?? 'The supplier';

	const cost = inFormCurrency(sug, offerCurrency);

	if (applied) {
		// The converted-from note sits on the row itself; this names the source.
		return (
			<div className="text-2xs text-text-3">
				Cost from {supplier}&apos;s price request
				{!hasSalePrice && (
					<span className="text-status-warn"> · set the sale price the client pays</span>
				)}
			</div>
		);
	}

	const production = days(sug.minProductionDays, sug.maxProductionDays);

	return (
		<div className="flex items-center gap-2 text-2xs text-text-3">
			<span>
				{supplier}&apos;s cost{' '}
				<span className="font-mono text-text-1">
					{cost
						? panelUnitMoney(cost.baseUnitPrice, offerCurrency)
						: panelUnitMoney(sug.baseUnitPrice, sug.currency)}
				</span>
				/unit
				{/* What they really quoted, whenever a rate was applied. */}
				{cost?.quoted &&
					` (quoted ${panelUnitMoney(cost.quoted.baseUnitPrice, cost.quoted.currency)})`}
				{production && ` · ${production} production`}
				{sug.shippingPrices.length > 0 &&
					` · shipping ${sug.shippingPrices.length === 1 ? 'for 1 method' : `for ${sug.shippingPrices.length} methods`}`}
			</span>
			{cost && (
				<button
					type="button"
					onClick={onUse}
					className="rounded-[var(--r-2)] border border-line-2 bg-surface-fill px-1.5 py-0.5 text-text-1 transition-colors hover:border-line-3"
				>
					Use
				</button>
			)}
		</div>
	);
}
